import { Preferences } from "@capacitor/preferences";

// Reconnection tokens for the live matches this device is seated in, keyed by
// room id. Which of those rooms are actually still reconnectable is the
// server's call (`GET /lobbies/mine`, discovered via Colyseus matchmaker
// metadata) — these tokens are only the credential colyseus.js needs to
// re-enter a held seat. Written while a match is under way (setup/playing)
// and kept fresh per connection; entries are removed when a match ends, a
// reconnect attempt fails, or the server stops listing the room. Same
// Capacitor Preferences backing as the auth tokens (localStorage on web,
// native prefs on mobile).

const ACTIVE_GAMES_KEY = "fortress.activeGames";

/** roomId → colyseus.js reconnection token (minted fresh per connection). */
export type ActiveGameTokens = Record<string, string>;

export async function getActiveGameTokens(): Promise<ActiveGameTokens> {
  const { value } = await Preferences.get({ key: ACTIVE_GAMES_KEY });
  if (!value) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: ActiveGameTokens = {};
    for (const [roomId, token] of Object.entries(parsed)) {
      if (typeof token === "string") out[roomId] = token;
    }
    return out;
  } catch {
    return {};
  }
}

async function writeTokens(tokens: ActiveGameTokens): Promise<void> {
  if (Object.keys(tokens).length === 0) {
    await Preferences.remove({ key: ACTIVE_GAMES_KEY });
    return;
  }
  await Preferences.set({
    key: ACTIVE_GAMES_KEY,
    value: JSON.stringify(tokens),
  });
}

export async function setActiveGameToken(
  roomId: string,
  reconnectionToken: string,
): Promise<void> {
  const tokens = await getActiveGameTokens();
  tokens[roomId] = reconnectionToken;
  await writeTokens(tokens);
}

export async function removeActiveGameToken(roomId: string): Promise<void> {
  const tokens = await getActiveGameTokens();
  if (!(roomId in tokens)) return;
  delete tokens[roomId];
  await writeTokens(tokens);
}
